import { getBootstrapConfig } from '#pplx/frontend/apps/ask/bootstrap-config/bootstrapConfig.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { getActiveAccountId } from '#pplx/frontend/browser-storage/activeAccount.js';
import { isPushEnabled, disablePush } from '#pplx/frontend/push-notifications/pushNotifications.js';
import { restorePush } from '#pplx/frontend/push-notifications/pushNotificationsRestore.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import { registerServiceWorker, unregisterServiceWorker, } from '#pplx/frontend/workers/service-worker/registration/index.js';
import { CURRENT_URL_REQUEST_MESSAGE_TYPE, NOTIFICATION_NAVIGATION_MESSAGE_TYPE, } from '#pplx/frontend/workers/service-worker/utils/metadata.js';
import React, { useEffect, useState } from 'react';
/**
 * Runs a service-worker for application caching.
 */
export const ServiceWorkerSideEffects = React.memo(function ServiceWorkerSideEffects() {
    const reason = 'ServiceWorker';
    const { status } = useSession();
    const { push } = useRouter();
    const [isServiceWorkerRegistered, setIsServiceWorkerRegistered] = useState(false);
    useEffect(() => {
        const bootstrapConfig = getBootstrapConfig();
        let cancelled = false;
        const register = async () => {
            await registerServiceWorker({
                serverEnvironment: bootstrapConfig.serverEnvironment,
                version: bootstrapConfig.version,
            });
            if (!cancelled) {
                setIsServiceWorkerRegistered(true);
            }
        };
        if (import.meta.env.VITE_SW_ENABLED_LOCAL) {
            register();
            return () => {
                cancelled = true;
            };
        }
        if (bootstrapConfig.env && bootstrapConfig.env !== 'local') {
            register();
        }
        else {
            (async () => {
                await disablePush({ reason });
                unregisterServiceWorker();
            })();
        }
        return () => {
            cancelled = true;
        };
    }, []);
    useEffect(() => {
        if (status === 'loading')
            return;
        if (status === 'unauthenticated') {
            (async () => {
                const isEnabled = await isPushEnabled().catch(() => false);
                if (!isEnabled)
                    return;
                await disablePush({ reason });
            })();
            return;
        }
        if (isServiceWorkerRegistered) {
            restorePush({ reason });
        }
    }, [isServiceWorkerRegistered, status]);
    useEffect(() => {
        if (!('serviceWorker' in navigator))
            return;
        const handleServiceWorkerMessage = (event) => {
            if (event.data?.type === NOTIFICATION_NAVIGATION_MESSAGE_TYPE) {
                const { url, accountId } = event.data;
                if (typeof url !== 'string')
                    return;
                const currentUrl = new URL(window.location.href);
                const target = new URL(url, currentUrl);
                // Recheck the account and route: either may have changed since the SW probe.
                const accepted = target.origin === currentUrl.origin &&
                    target.pathname === currentUrl.pathname &&
                    (accountId === undefined || accountId === getActiveAccountId());
                event.ports[0]?.postMessage(accepted);
                if (accepted)
                    push(`${target.pathname}${target.search}${target.hash}`, {}, 'notification');
                return;
            }
            if (event.data?.type !== CURRENT_URL_REQUEST_MESSAGE_TYPE)
                return;
            // Reply with this tab's active account too, so the SW can route a notification click to
            // the tab that's actually on the recipient account (multi-account web push).
            event.ports[0]?.postMessage({
                url: window.location.href,
                accountId: getActiveAccountId(),
            });
        };
        navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
        return () => {
            navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
        };
    }, [push]);
    return null;
});
//# sourceMappingURL=ServiceWorkerSideEffects.js.map